import React, { useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Brand } from '../utils/brandModelStorage';
import { formatSimilarity, getMatchMessage } from '../utils/fuzzyMatch';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';

interface DidYouMeanDialogProps {
  visible: boolean;
  userInput: string;
  suggestedBrand: (Brand & { similarity: number; matchType: 'exact' | 'alias' | 'fuzzy' }) | null;
  onUseExisting: () => void;
  onCreateNew: () => void;
  onCancel: () => void;
}

/**
 * Dialog that appears when user tries to create a brand similar to an existing one
 * Prevents duplicates by suggesting existing brands
 */
export function DidYouMeanDialog({
  visible,
  userInput,
  suggestedBrand,
  onUseExisting,
  onCreateNew,
  onCancel,
}: DidYouMeanDialogProps) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        dialogContent: {
          maxWidth: 450,
        },
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.md,
          marginBottom: theme.spacing.lg,
        },
        title: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
          flex: 1,
        },
        body: {
          gap: theme.spacing.lg,
        },
        messageText: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
        },
        label: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          marginBottom: theme.spacing.xs,
        },
        inputBox: {
          padding: theme.spacing.md,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        userInputText: {
          ...theme.typography.lg,
          color: theme.colors.textPrimary,
        },
        suggestionBox: {
          padding: theme.spacing.lg,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.colors.accent,
          borderRadius: theme.radius.radiusMd,
        },
        suggestionHeader: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        },
        similarityBadge: {
          paddingVertical: 2,
          paddingHorizontal: theme.spacing.sm,
          backgroundColor: theme.colors.accent,
          borderRadius: theme.radius.radiusSm,
        },
        similarityText: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.backgroundPrimary,
          letterSpacing: 0,
        },
        brandName: {
          ...theme.typography.h3,
          color: theme.colors.accent,
          marginTop: theme.spacing.xs,
          marginBottom: theme.spacing.sm,
        },
        aliasesContainer: {
          marginTop: theme.spacing.sm,
          paddingTop: theme.spacing.sm,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
        },
        aliasesLabel: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          marginBottom: theme.spacing.xxs,
          letterSpacing: 0,
        },
        aliasesText: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
        warningBox: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.sm,
          padding: theme.spacing.md,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.colors.destructive,
          borderRadius: theme.radius.radiusMd,
        },
        warningText: {
          flex: 1,
          ...theme.typography.sm,
          color: theme.colors.destructive,
        },
        actions: {
          gap: theme.spacing.md,
          marginTop: theme.spacing.xl,
          paddingTop: theme.spacing.lg,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
        },
        createNewButton: {
          borderColor: theme.colors.accent,
        },
        createNewButtonText: {
          color: theme.colors.accent,
        },
      }),
    [theme],
  );

  if (!suggestedBrand) return null;

  const aliases = (suggestedBrand.aliases as string[]) || [];
  const matchMessage = getMatchMessage(suggestedBrand.matchType);
  const isExactOrAlias = suggestedBrand.matchType === 'exact' || suggestedBrand.matchType === 'alias';

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={visible}
      onRequestClose={onCancel}
    >
      <View style={commonStyles.modalOverlay}>
        <View style={[commonStyles.modalContent, styles.dialogContent]}>
          {/* Header */}
          <View style={styles.header}>
            <Ionicons name="warning" size={28} color={theme.colors.accent} />
            <Text style={styles.title}>Similar Brand Found</Text>
          </View>

          {/* Body */}
          <View style={styles.body}>
            <Text style={styles.messageText}>{matchMessage}</Text>

            {/* User Input */}
            <View style={styles.inputBox}>
              <Text style={styles.label}>You typed</Text>
              <Text style={styles.userInputText}>"{userInput}"</Text>
            </View>

            {/* Suggested Brand */}
            <View style={styles.suggestionBox}>
              <View style={styles.suggestionHeader}>
                <Text style={styles.label}>Did you mean</Text>
                {suggestedBrand.matchType === 'fuzzy' && (
                  <View style={styles.similarityBadge}>
                    <Text style={styles.similarityText}>
                      {formatSimilarity(suggestedBrand.similarity)} match
                    </Text>
                  </View>
                )}
              </View>

              <Text style={styles.brandName}>{suggestedBrand.name}</Text>

              {aliases.length > 0 && (
                <View style={styles.aliasesContainer}>
                  <Text style={styles.aliasesLabel}>Also known as</Text>
                  <Text style={styles.aliasesText}>{aliases.join(', ')}</Text>
                </View>
              )}
            </View>

            {/* Warning for exact matches */}
            {isExactOrAlias && (
              <View style={styles.warningBox}>
                <Ionicons name="alert-circle" size={16} color={theme.colors.destructive} />
                <Text style={styles.warningText}>
                  Creating a duplicate will cause confusion and data quality issues.
                </Text>
              </View>
            )}
          </View>

          {/* Actions */}
          <View style={styles.actions}>
            <TouchableOpacity
              style={commonStyles.secondaryButton}
              onPress={onCancel}
            >
              <Text style={commonStyles.secondaryButtonText}>Cancel</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={commonStyles.primaryButton}
              onPress={onUseExisting}
            >
              <Ionicons name="checkmark-circle" size={20} color={theme.colors.backgroundPrimary} />
              <Text style={commonStyles.primaryButtonText}>
                Use "{suggestedBrand.name}"
              </Text>
            </TouchableOpacity>

            {!isExactOrAlias && (
              <TouchableOpacity
                style={[commonStyles.secondaryButton, styles.createNewButton]}
                onPress={onCreateNew}
              >
                <Text style={[commonStyles.secondaryButtonText, styles.createNewButtonText]}>
                  No, create "{userInput}"
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
}

